import { Flex } from '@auviknetworks/iceberg-ui';
import { useTheme } from '@mui/material/styles';
import Image from 'application/Shared/Components/Image';
import { RenderNoImage } from 'application/Shared/Components/RenderNoImage';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Route } from 'application/Shared/Helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { usePanelContext } from '../Panels/PanelContext';
import { type ChartPanel as ChartPanelType } from '../types';
import { templateInterpolation } from './templateInterpolation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ImageIcon = ({ context }: { context: any }) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    const displayName = context.name || context.appName;

    return (
        <Link
            to={`${baseAdminRoute}/application/${context.appId}`}
            aria-label={t('Application.navigateTo', { name: displayName })}
        >
            {!context.img ? (
                <RenderNoImage name={displayName} />
            ) : (
                <Image
                    aria-label={displayName}
                    imageId={context.img}
                    style={{ height: 16, width: 16 }}
                />
            )}
        </Link>
    );
};

interface AxisFormat {
    angle?: number;
    tickFormatter?: string;
}

type CustomizedAxisTickProps = {
    x: number;
    y: number;
    index: number;
    format: AxisFormat;
    axis: 'xAxis' | 'yAxis';
    payload: { value: string };
};

export const Tick = ({
    x,
    y,
    index,
    axis,
    payload: { value }
}: CustomizedAxisTickProps) => {
    const { palette } = useTheme();
    const {
        queryResults: { data },
        schema: { format }
    } = usePanelContext<ChartPanelType>();

    const {
        angle = axis === 'xAxis' ? -35 : 0,
        tickFormatter = '{{name}}',
        tick = 'data'
    } = format?.[axis] || {};

    const context = { ...data.series[index], value };

    return (
        <g transform={`translate(${x},${y})`}>
            {tick === 'image' ? (
                <foreignObject
                    x={axis === 'xAxis' ? -8 : -16}
                    y={axis === 'xAxis' ? 0 : -8}
                    width={75}
                    height={24}
                >
                    {/* TODO: we may add control over image sources to the formatter */}
                    <Flex width="fit-content">
                        <DarkTooltip
                            arrow
                            placement="bottom"
                            title={context.name || context.appName}
                        >
                            <Flex alignContents="center" width="fit-content">
                                <ImageIcon context={context} />
                            </Flex>
                        </DarkTooltip>
                    </Flex>
                </foreignObject>
            ) : (
                <text
                    x={0}
                    y={0}
                    // We are reusing the same tick component for both x and y axes
                    // so we need to adjust the position of the text based on the axis
                    dy={axis === 'xAxis' ? 16 : 4}
                    dx={axis === 'xAxis' ? 8 : -4}
                    textAnchor="end"
                    fill={palette.text.primary}
                    transform={`rotate(${angle})`}
                >
                    {tick === 'data' || !tickFormatter
                        ? value
                        : templateInterpolation(tickFormatter, context)}
                </text>
            )}
        </g>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AxisXTick = (props: any) => <Tick {...props} axis="xAxis" />;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AxisYTick = (props: any) => <Tick {...props} axis="yAxis" />;

/**
 * Renders a custom label for vertical layout charts
 * @param props - The props object containing:
 *   @param {number} props.x - X coordinate for label placement
 *   @param {number} props.y - Y coordinate for label placement
 *   @param {number} props.height - Height of the label container
 *   @param {any} props.value - Value to display in the label
 *   @param {number} props.width - Width of the label container
 * @returns JSX element rendering the label value in a flex container
 */
export const renderCustomVerticalLayoutLabel = ({
    x,
    y,
    height,
    value,
    width
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => (
    <foreignObject x={x + width + 10} y={y + height / 4} width={75} height={24}>
        <Flex alignItems="center">{value}</Flex>
    </foreignObject>
);
